import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Layers,
  Zap,
  Globe,
  BarChart3,
  Users,
  Shield,
  ArrowRight,
  Check,
  Star,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const features = [
  {
    icon: Zap,
    title: 'AI-Powered Generation',
    description:
      'Our AI analyzes your domain name and generates content that actually sounds human. No generic templates.',
  },
  {
    icon: Globe,
    title: 'One-Click Deployment',
    description:
      'Connect your Cloudflare account and deploy instantly. SSL, CDN, and global distribution included.',
  },
  {
    icon: BarChart3,
    title: 'Real-Time Analytics',
    description:
      'Track every visitor, click, and conversion. Know exactly how your domains are performing.',
  },
  {
    icon: Users,
    title: 'Lead Management',
    description:
      'Capture and manage leads from all your sites in one place. Export, track, and convert.',
  },
  {
    icon: Shield,
    title: 'YOLO Mode',
    description:
      'Have 50 domains? Queue them all and let AI handle everything. Review and deploy in bulk.',
  },
  {
    icon: Star,
    title: 'Premium Designs',
    description:
      'Sites that look like they cost $5,000, not $5. Modern aesthetics that convert visitors.',
  },
]

const pricing = [
  {
    name: 'Free',
    price: 0,
    interval: 'mo',
    description: 'Perfect for trying it out',
    features: ['2 domain sites', 'Basic analytics', 'Lead capture', 'Community support'],
    cta: 'Get Started',
    href: '/register',
  },
  {
    name: 'Pro',
    price: 29,
    interval: 'mo',
    description: 'For domain investors',
    features: [
      '10 domain sites',
      'Advanced analytics',
      'Lead management',
      'YOLO batch mode',
      'Custom branding',
      'Priority support',
    ],
    cta: 'Start Free Trial',
    href: '/register',
    popular: true,
  },
  {
    name: 'Lifetime',
    price: 79,
    interval: 'once',
    description: 'For focused portfolios',
    features: [
      '5 domain sites',
      'One-time payment',
      'All site templates',
      'Lead management',
      'Full analytics dashboard',
      'Priority support',
    ],
    cta: 'Buy Lifetime',
    href: '/register',
  },
]

export function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg gradient-bg">
                <Layers className="h-5 w-5 text-white" />
              </div>
              <span className="font-display font-bold text-lg">UnparkMyDomain</span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/login" className="btn-ghost">
                Login
              </Link>
              <Link to="/register" className="btn-primary btn-sm">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-50" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="badge badge-primary mb-4">
              No more parked domain pages
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight">
              Turn Idle Domains Into
              <br />
              <span className="gradient-text">Revenue Machines</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              AI-powered site generation that transforms your unused domains into
              professional landing pages, lead capture sites, and monetization machines.
              No coding required.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="btn-primary btn-lg">
                Start For Free
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="#demo" className="btn-outline btn-lg">
                See It In Action
              </Link>
            </div>

            <p className="mt-4 text-sm text-muted-foreground">
              No credit card required. 2 free domains included.
            </p>
          </motion.div>

          {/* Hero Image/Preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-16 relative"
          >
            <div className="relative mx-auto max-w-5xl">
              <div className="rounded-2xl border border-border bg-card/50 backdrop-blur p-2 shadow-2xl shadow-primary/10">
                <div className="rounded-xl bg-card overflow-hidden">
                  {/* Browser chrome */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-destructive/50" />
                      <div className="w-3 h-3 rounded-full bg-accent/50" />
                      <div className="w-3 h-3 rounded-full bg-success/50" />
                    </div>
                    <div className="flex-1 flex justify-center">
                      <div className="px-4 py-1 rounded-md bg-muted text-xs text-muted-foreground">
                        yourdomain.com
                      </div>
                    </div>
                  </div>
                  {/* Screenshot placeholder */}
                  <div className="aspect-video bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 flex items-center justify-center">
                    <div className="text-center">
                      <div className="h-16 w-16 mx-auto mb-4 rounded-2xl gradient-bg flex items-center justify-center">
                        <Zap className="h-8 w-8 text-white" />
                      </div>
                      <p className="text-2xl font-display font-bold mb-2">
                        Your AI-Generated Site
                      </p>
                      <p className="text-muted-foreground">
                        Deployed and live in under 5 minutes
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-card/50" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
              Everything You Need to Monetize
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From AI analysis to deployment, we handle the entire process.
              You just pick the strategy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="card-hover"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                  <feature.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20" id="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
              Domain to Revenue in 3 Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Add Your Domain',
                description:
                  'Connect your Cloudflare account and add your domain. We verify ownership automatically.',
              },
              {
                step: '02',
                title: 'AI Analyzes & Generates',
                description:
                  'Our AI analyzes your domain name, suggests the best monetization strategy, and generates content.',
              },
              {
                step: '03',
                title: 'Deploy & Monetize',
                description:
                  'Preview your site, make any tweaks, and deploy with one click. Start capturing leads immediately.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative"
              >
                <div className="text-7xl font-display font-bold text-primary/10 mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-card/50" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              Start free, scale as you grow. No hidden fees.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricing.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={cn(
                  'card relative',
                  plan.popular && 'border-primary shadow-lg shadow-primary/10'
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="badge badge-primary">Most Popular</span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-display font-bold">${plan.price}</span>
                    <span className="text-muted-foreground">
                      {plan.interval === 'once' ? ' once' : '/mo'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={plan.href}
                  className={cn(
                    'w-full',
                    plan.popular ? 'btn-primary' : 'btn-outline'
                  )}
                >
                  {plan.cta}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
            Stop Letting Domains Collect Dust
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join domain investors who are turning their idle portfolios into passive
            income streams.
          </p>
          <Link to="/register" className="btn-primary btn-lg">
            Get Started Free
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg gradient-bg">
                <Layers className="h-5 w-5 text-white" />
              </div>
              <span className="font-display font-bold">UnparkMyDomain</span>
            </div>

            <div className="flex gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">
                Privacy
              </a>
              <a href="#" className="hover:text-foreground transition-colors">
                Terms
              </a>
              <a href="#" className="hover:text-foreground transition-colors">
                Contact
              </a>
            </div>

            <p className="text-sm text-muted-foreground">
              © 2026 UnparkMyDomain. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
